import {
  Box,
  Button,
  Grid,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useMediaQuery,
  VStack,
} from "@chakra-ui/react";
import useColorAccent from "../../hooks/useColorAccent";
import { useHlsAudioActions } from "../../wrappers/hlsAudio";
import useReactiveQuery from "../../hooks/data/useReactiveQuery";
import useCopy from "../../helpers/useCopy";
import { useReportManager } from "../../wrappers/report";
import usePlaylist from "../../hooks/data/usePlaylist";
import useUserStore from "../../store/userStore";
import {
  Image,
  RadioCard,
  RadioCardGroup,
  Tooltip,
  useCustomToast,
} from "../reusables";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import useImageCompressor from "../../hooks/useImageCompressor";
import { useTracker } from "../../wrappers/tracker";
import dayjs from "../../helpers/dayJs";
import {
  BiDotsVertical,
  BiPencil,
  BiPlay,
  BiPlus,
  BiTrash,
} from "react-icons/bi";
import { FiClock } from "react-icons/fi";
import AnimatedNumber from "react-animated-numbers";
import User from "../user";
import {
  RiBookmarkLine,
  RiShareForwardFill,
  RiThumbDownFill,
  RiThumbDownLine,
  RiThumbUpFill,
  RiThumbUpLine,
} from "react-icons/ri";
import TextEditorParser from "../textEditor/textEditorParser";
import useProducts from "../../hooks/data/useProducts";
import { shallow } from "zustand/shallow";
import { IoBug } from "react-icons/io5";
import { FieldArray, Form, Formik } from "formik";
import useReactions from "../../hooks/data/useReactions";
import { useFirebase } from "../../wrappers/firebase";
import { httpsCallable } from "firebase/functions";

export default function IsolatedProduct(props) {
  const [isMobile] = useMediaQuery("(max-width: 600px)");

  const { attachImage, accentColor, counterColor } = useColorAccent();

  const { playTrack } = useHlsAudioActions();

  const { get } = useReactiveQuery();
  const { functions } = useFirebase();

  const product = get(["products", props.id]);

  const { copy } = useCopy();

  const { triggerReportContent } = useReportManager();
  const { addItemToPlaylist } = usePlaylist();

  // const [userPlaylists, setUserPlaylists] = useState();
  const {
    username: stateUsername,
    id: stateUserId,
    stripeCustomerId,
  } = useUserStore((state) => state.user, shallow);

  const isOwner = product?.user?.username === stateUsername;
  const [variationPair, setVariationPair] = useState();
  const navigate = useNavigate();
  const [isDeleteOpen, setDeleteOpen] = useState();
  const { findClosestMatch } = useImageCompressor();

  const imageContainerRef = useRef();
  const [currentImage, setCurrentImage] = useState();
  const {
    addLikeReaction,
    removeLikeReaction,
    addDislikeReaction,
    removeDislikeReaction,
  } = useReactions({
    type: "product",
    contentId: product?.id,
    contentUserId: product?.user?.id,
  });
  const toast = useCustomToast();

  useEffect(() => {
    if (product?.images) {
      setCurrentImage(product?.images[0]);
    }
  }, [product?.images]);

  async function updateLikeReaction() {
    if (product.isLikedByUser) {
      removeLikeReaction();
    } else {
      addLikeReaction();
    }
  }

  async function updateDislikeReaction() {
    if (product.isDislikedByUser) {
      removeDislikeReaction();
    } else {
      addDislikeReaction();
    }
  }

  function goToEdit() {
    navigate("/create/product", {
      state: { product },
    });
  }

  const formattedDate =
    product?.createdAt && product?.createdAt?.toDate
      ? dayjs(product?.createdAt.toDate()).calendar()
      : "";

  const imageFileName = useMemo(
    () =>
      findClosestMatch(currentImage?.compressed, imageContainerRef)?.fileName,
    [currentImage, imageContainerRef.current]
  );

  function toggleDelete() {
    setDeleteOpen((prev) => !prev);
  }

  function performDelete() {
    deletePost({ productId: product.id });
    navigate("/");
  }

  function validateCheckoutForm(values) {
    if (!values) return;
    if (!values.selectedVariations) return;

    const errors = {};
    const validVariationPair = getCheckoutVariation(values.selectedVariations);
    if (!validVariationPair || validVariationPair?.quantity <= 0) {
      errors.selectedVariations = "Out of stock!";
    }
    setVariationPair(validVariationPair);
    return errors;
  }

  function getCheckoutVariation(selectedVariations) {
    const variationPair = Object.keys(selectedVariations)?.map(
      (key) => `${key}:${selectedVariations[key]}`
    );

    const validVariationPair = product?.pricingData?.find((pricingData) =>
      variationPair?.every((variation) =>
        pricingData.variations.some(
          (pricingVariation) => pricingVariation.value === variation
        )
      )
    );

    return validVariationPair;
  }

  const getInitialValues = useMemo(() => {
    if (product?.id === props?.id) {
      console.log("Product is:", product);
      const selectedVariations = {};
      product.variations?.map((parentVariation) => {
        selectedVariations[parentVariation.name] =
          parentVariation.variations[0].name;
      });

      return {
        selectedVariations,
        isShippingModalOpen: false,
        shippingMethodId: product?.shippingMethods?.[0]?.id,
      };
    }
  }, [props?.id, product?.id]);

  async function goToCheckout(values) {
    const variationPair = getCheckoutVariation(values.selectedVariations);
    const data = {
      variationPair,
      success_url: window.location.href,
      stripeCustomerId,
      productId: product.stripeProductId,
      shippingMethodId: values.shippingMethodId,
      metadata: {
        sellerUid: product.user.id,
        productId: product.id,
        variation: variationPair.variations
          .map((variation) => variation.label)
          .join(", "),
      },
    };

    const checkoutFunc = httpsCallable(functions, "createStoreCheckout");
    try {
      const { data: checkoutObj } = await checkoutFunc(data);
      // copy(checkoutObj.id);
      window.open(checkoutObj.url, "_self");
    } catch (ex) {
      console.log("Checkout error is:", ex);
      toast({
        status: "error",
        title: "Could not go to checkout",
        description: "Something went wrong! Please try again later.",
      });
    }
  }

  return (
    <VStack w="full">
      {isOwner && (
        <HStack
          w="full"
          justify={"space-between"}
          align={"center"}
          bg="#252525"
          border="1px solid #3e3e46"
          borderRadius={"md"}
          px="2"
          pr="1"
          py="1"
        >
          <Text fontSize={"xs"} color={"gray.400"}>
            You can modify this product
          </Text>
          <Menu placement="bottom">
            <MenuButton
              as={Button}
              size="sm"
              // icon={<BiPencil />}
              variant="nav"
              fontSize={"xs"}
              rightIcon={<BiDotsVertical />}
              border="1px solid #3e3e46"
            >
              Actions
            </MenuButton>
            <MenuList>
              <MenuItem onClick={goToEdit}>
                <BiPencil />
                <Text>Edit</Text>
              </MenuItem>
              <MenuItem color="red.400" onClick={toggleDelete}>
                <BiTrash />
                <Text>Delete product</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      )}
      <Grid
        w="full"
        h="max-content"
        // gridTemplateColumns={"70% 30%"}
        gridTemplateColumns={!isMobile && "minmax(0, 0.6fr) minmax(0, 0.4fr)"}
        gridColumnGap={"3"}
        gridRowGap={"3"}
        gridTemplateRows={isMobile ? "repeat(4, auto)" : "repeat(3, auto)"}
        overflow={"visible"}
        // overflowX={"hidden"}
      >
        <VStack h="max-content" gridArea={"1 / 1 / 2 / 2"} minW={"0"}>
          <Box bg={`${accentColor}40`} w="full" borderRadius={"lg"}>
            <Image
              ref={imageContainerRef}
              firebaseStoragePath={
                imageFileName
                  ? `photos/${product?.user?.id}/products/${product?.id}/images/${imageFileName}`
                  : null
              }
              borderRadius={"lg"}
              w="full"
              h="280px"
              minW="0"
              onLoad={attachImage}
              objectFit={"contain"}
            />
          </Box>
          {product?.images?.length > 1 && (
            <HStack h="60px" w="full" overflow={"scroll"}>
              {product.images?.map((image, index) => {
                return (
                  <Image
                    key={`product_image_corousel_${index}`}
                    firebaseStoragePath={`photos/${product?.user?.id}/products/${product?.id}/images/${image.compressed[0]?.fileName}`}
                    h="60px"
                    w="60px"
                    borderRadius={"md"}
                    border="2px solid #303036"
                    _hover={{
                      borderColor: "blue.400",
                    }}
                    cursor={"pointer"}
                    onClick={() => setCurrentImage(image)}
                  />
                );
              })}
            </HStack>
          )}
        </VStack>
        {/* <VStack w="full" gridArea={isMobile ? "4 / 1 / 5 / 2" : "1 / 2 / 2 / 3"}> */}

        <Formik
          initialValues={getInitialValues}
          onSubmit={(values) => goToCheckout(values)}
          validate={validateCheckoutForm}
          validateOnChange={true}
          validateOnMount={true}
          enableReinitialize
        >
          {({
            setFieldValue,
            isSubmitting,
            isValid,
            values,
            errors,
            handleSubmit,
          }) =>
            values?.selectedVariations && (
              <Form
                style={{
                  width: "100%",
                }}
              >
                <Grid
                  gridArea={isMobile ? "4 / 1 / 5 / 2" : "1 / 2 / 2 / 3"}
                  //   border="1px solid #3e3e46"
                  borderRadius={"lg"}
                  h="full"
                  minW={!isMobile && "0px"}
                  p="2"
                  //   px="3"
                  pr="1"
                  // pb="0"
                  align={"flex-start"}
                  spacing={"3.5"}
                  pos="relative"
                  gridTemplateRows={"1fr auto"}
                  // rowGap={"4"}
                >
                  <VStack w="full" h="full" align="flex-start" spacing={"3"}>
                    {product?.variations?.map(
                      (parentVariation, parentIndex) => (
                        <VStack
                          key={`product_variation_${parentVariation.name}_${parentIndex}`}
                          align={"flex-start"}
                        >
                          <Text fontSize={"sm"} fontWeight={"medium"}>
                            Select {parentVariation.name}
                          </Text>
                          <RadioCardGroup
                            defaultValue={parentVariation.variations[0].name}
                            onChange={(val) => {
                              setFieldValue(
                                `selectedVariations[${parentVariation.name}]`,
                                val
                              );
                            }}
                          >
                            {parentVariation.variations?.map(
                              (childVariation) => (
                                <RadioCard
                                  key={`product_variation_${parentVariation.name}_${parentIndex}_${childVariation.name}`}
                                  py="1"
                                  px="3"
                                  spacing={"1"}
                                  value={childVariation.name}
                                  align={"center"}
                                >
                                  <Text fontWeight={"semibold"}>
                                    {childVariation.name}
                                  </Text>
                                </RadioCard>
                              )
                            )}
                          </RadioCardGroup>
                        </VStack>
                      )
                    )}
                  </VStack>
                  <VStack spacing={"1"} w="full" pt="2" align={"flex-start"}>
                    {errors && (
                      <Text color="red.400" fontSize={"xs"}>
                        {errors.selectedVariations}
                      </Text>
                    )}
                    <HStack align={"center"} spacing={"1"}>
                      <Text fontSize={"sm"}>$</Text>
                      <Text fontSize={"2xl"} fontWeight={"semibold"}>
                        {getCheckoutVariation(values.selectedVariations)
                          ?.amount || 0}
                      </Text>
                    </HStack>
                    <Button
                      size="sm"
                      isDisabled={!isValid}
                      w="full"
                      onClick={() => setFieldValue("isShippingModalOpen", true)}
                      bg="green.400"
                      _hover={{
                        bg: "white",
                      }}
                    >
                      Buy Now
                    </Button>
                  </VStack>
                </Grid>
                <Modal
                  size={"md"}
                  isOpen={values.isShippingModalOpen}
                  isCentered
                  onClose={() => setFieldValue("isShippingModalOpen", false)}
                >
                  <ModalOverlay />
                  <ModalContent>
                    <ModalHeader>
                      Continue Checkout
                      <ModalCloseButton />
                    </ModalHeader>
                    <ModalBody color={"white"} fontSize={"sm"} px="2">
                      {product?.productType === "physical" ? (
                        <VStack w={"full"} align={"flex-start"}>
                          <Text>Please select a shipping method:</Text>
                          <RadioCardGroup
                            defaultValue={values.shippingMethodId}
                            onChange={(val) =>
                              setFieldValue(`shippingMethodId`, val)
                            }
                            w="full"
                          >
                            {product?.shippingMethods?.map((method) => (
                              <RadioCard
                                key={`product_shipping_${method.id}`}
                                py="1"
                                px="3"
                                spacing={"0"}
                                value={method.id}
                                align={"flex-start"}
                                w="full"
                              >
                                <Text fontWeight={"semibold"} fontSize={"sm"}>
                                  {method.name}
                                </Text>
                                <Text fontSize={"xs"} color="gray.400">
                                  Receive delivery within {method.deliveryDays}{" "}
                                  days
                                </Text>
                                {method.amount && (
                                  <Text fontSize={"sm"} pt="1">
                                    + ${method.amount}
                                  </Text>
                                )}
                              </RadioCard>
                            ))}
                          </RadioCardGroup>
                        </VStack>
                      ) : (
                        <VStack w={"full"} align={"flex-start"}>
                          <Text>
                            After your purchase, you will find the deliverables
                            of this product in "My Orders" menu.
                          </Text>
                        </VStack>
                      )}
                    </ModalBody>
                    <ModalFooter>
                      <Button
                        size="sm"
                        variant={"nav"}
                        onClick={() =>
                          setFieldValue("isShippingModalOpen", false)
                        }
                        isDisabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSubmit}
                        isLoading={isSubmitting}
                      >
                        Proceed
                      </Button>
                    </ModalFooter>
                  </ModalContent>
                </Modal>
              </Form>
            )
          }
        </Formik>
        {/* </VStack> */}
        <VStack
          gridArea={isMobile ? "2 / 1 / 3 / 2" : "2 / 1 / 3 / 3"}
          // mt="3"
          borderRadius={"md"}
          w="full"
          align={"flex-start"}
          py="1"
          pb="3"
          spacing={isMobile ? "2" : "4"}
          minW={"0"}
        >
          <VStack w="full" align={"flex-start"} color={counterColor}>
            <Text fontSize={"xl"} fontWeight={"medium"}>
              {product?.name}
            </Text>
            {product?.track?.tags?.map((tag, index) => (
              <Text
                key={`product_tag_${index}`}
                fontSize={"xs"}
                fontWeight={"semibold"}
                bg={`${accentColor}50`}
                py="0.5"
                px="2"
                borderRadius={"md"}
              >
                {tag}
              </Text>
            ))}
          </VStack>
          <HStack justify={"space-between"} w="full">
            <User.Regular
              {...product?.user}
              allowNavigate={true}
              extra={<>- {formattedDate}</>}
            />

            <HStack
              borderRadius={"full"}
              overflow={"hidden"}
              maxW="fit-content"
            >
              <Button
                variant={"nav"}
                cursor={"pointer"}
                borderRadius={"full"}
                onClick={updateLikeReaction}
                fontSize={"sm"}
                bg={`${accentColor}50`}
                _hover={{
                  bg: `${accentColor}70`,
                }}
                aspectRatio={
                  !product?.likeCount || product?.likeCount <= 0
                    ? "1 / 1"
                    : "auto"
                }
              >
                <HStack justify={"center"}>
                  {product?.isLikedByUser ? (
                    <RiThumbUpFill />
                  ) : (
                    <RiThumbUpLine />
                  )}
                  {product?.likeCount && <Text>{product?.likeCount}</Text>}
                </HStack>
              </Button>
              {!isMobile && (
                <>
                  <IconButton
                    variant={"nav"}
                    cursor={"pointer"}
                    borderRadius={"full"}
                    onClick={updateDislikeReaction}
                    icon={
                      product?.isDislikedByUser ? (
                        <RiThumbDownFill />
                      ) : (
                        <RiThumbDownLine />
                      )
                    }
                    bg={`${accentColor}50`}
                    _hover={{
                      bg: `${accentColor}70`,
                    }}
                  />
                  <Tooltip label={"Share"} placement="bottom">
                    <IconButton
                      variant={"nav"}
                      cursor={"pointer"}
                      borderRadius={"full"}
                      // onClick={updateDislikeReaction}
                      icon={<RiShareForwardFill />}
                      bg={`${accentColor}50`}
                      _hover={{
                        bg: `${accentColor}70`,
                      }}
                      onClick={() => copy(window.location.href)}
                    />
                  </Tooltip>
                  {!isOwner && (
                    <Menu placement="bottom-end">
                      <MenuButton
                        as={IconButton}
                        icon={<BiDotsVertical />}
                        variant={"nav"}
                        bg={`${accentColor}50`}
                        borderRadius={"full"}
                      />
                      <MenuList>
                        <MenuItem
                          color="red.400"
                          onClick={triggerReportContent}
                        >
                          <IoBug />
                          <Text>Report this content</Text>
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  )}
                </>
              )}
            </HStack>
          </HStack>
        </VStack>
        <Box
          gridArea={isMobile ? "3 / 1 / 4 / 2" : "3 / 1 / 4 / 3"}
          px={isMobile ? "1.5" : "0"}
          minW={"0"}
          overflowX={"hidden"}
        >
          <TextEditorParser
            text={product?.description}
            validSearchResults={product?.description_validSearchResults}
          />
        </Box>
        {isOwner && (
          <Modal size="sm" isOpen={isDeleteOpen} onClose={toggleDelete}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                Delete this product?
                <ModalCloseButton />
              </ModalHeader>
              <ModalBody px="2" fontSize={"sm"}>
                Are you sure you want to delete this product? This cannot be
                undone.
              </ModalBody>
              <ModalFooter>
                <Button size="sm" variant={"nav"} onClick={toggleDelete}>
                  Cancel
                </Button>
                <Button size="sm" onClick={performDelete}>
                  Delete
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
      </Grid>
    </VStack>
  );
}
